import PropTypes from "prop-types"
import React, { useEffect, useState, useCallback, useMemo } from "react"
import { BrowserRouter as Router, Redirect } from "react-router-dom"
import { connect } from "react-redux"
import io from "socket.io-client"
import axios from "axios"
import CustomSwitch from "./CustomSwitch"
import Loader from "./components/Loader/loader"
import IdleTimer from "./components/IdleTimer/IdleTimer"
import GlobalLeadModal from "./components/GlobalLeadModal"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { userRoutes, authRoutes } from "./routes/allRoutes"
import Authmiddleware from "./routes/middleware/Authmiddleware"
import HorizontalLayout from "./components/HorizontalLayout"
import NonAuthLayout from "./components/NonAuthLayout"
import { getCurrentUser, setCurrentUser, setPermission } from "./helpers/Utils"
import { useHistory } from "react-router-dom"
import "./assets/scss/theme.scss"
import notificationSound from "./assets/sounds/notification-sound.wav"

// Constants
const SOCKET_URL = "https://sockets.klikcrm.com/"
const ROLES = {
  ADMIN: "Administrator",
  ACCOUNTANT: "Accountant",
}
const TOAST_CONFIG = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
}
const NOTIFICATION_TOAST_CONFIG = {
  ...TOAST_CONFIG,
  autoClose: 300000,
}
const SUCCESS_TOAST_CONFIG = {
  ...TOAST_CONFIG,
  position: "top-center",
  hideProgressBar: false,
}
const TIMING = {
  TOAST_CLEANUP_DELAY: 100,
  SUCCESS_RESET_DELAY: 5500,
  ERROR_RESET_DELAY: 5500,
  REDIRECT_DELAY: 2000,
}

const App = props => {
  const history = useHistory()
  const [audio] = useState(new Audio(notificationSound))
  const [showCloseAllButton, setShowCloseAllButton] = useState(false)
  const [toastShown, setToastShown] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [socketInitialized, setSocketInitialized] = useState(false)
  const [userReady, setUserReady] = useState(false)

  // Memoized values
  const subDomain = useMemo(() => window.location.host.split(".")[0], [])
  const isToken = useMemo(() => window.location.href.split("token=")[1], [])
  // Don't memoize companyName - it needs to update when localStorage changes
  const companyName = localStorage.getItem("company_name")

  function getLayout() {
    return HorizontalLayout
  }

  const Layout = getLayout()

  // Memoized callbacks
  const closeAllToasts = useCallback(() => {
    toast.dismiss()
    setShowCloseAllButton(false)
  }, [])

  const handleToastClose = useCallback(() => {
    setTimeout(() => {
      if (document.querySelectorAll(".Toastify__toast").length === 0) {
        setShowCloseAllButton(false)
      }
    }, TIMING.TOAST_CLEANUP_DELAY)
  }, [])

  const showNotificationToast = useCallback(
    message => {
      audio.play()
      setShowCloseAllButton(true)
      return toast.success(message, {
        ...NOTIFICATION_TOAST_CONFIG,
        onClose: handleToastClose,
      })
    },
    [audio, handleToastClose]
  )

  // Helper functions
  const getRedirectPath = useCallback(roleName => {
    switch (roleName) {
      case ROLES.ADMIN:
        return "/dashboard"
      case ROLES.ACCOUNTANT:
        return "/accounts"
      default:
        return "/followups-today"
    }
  }, [])

  const handleTokenSuccess = useCallback(
    (userData, message) => {
      setCurrentUser(userData)
      setPermission(userData.permissions)
      localStorage.setItem("ShowImportant", userData?.show_important_date || "")

      const redirectPath = getRedirectPath(userData.role_name)

      // Show success toast only once without using showToast wrapper
      if (!toastShown) {
        setShowCloseAllButton(true)
        toast.success(message, {
          ...SUCCESS_TOAST_CONFIG,
          onClose: handleToastClose,
        })
        setToastShown(true)
        setTimeout(() => setToastShown(false), TIMING.SUCCESS_RESET_DELAY)
      }

      setTimeout(() => {
        if (redirectPath.startsWith("/")) {
          window.location.href = redirectPath
        } else {
          window.location.href = redirectPath
        }
      }, TIMING.REDIRECT_DELAY)
    },
    [toastShown, handleToastClose, getRedirectPath]
  )

  const handleTokenError = useCallback(
    message => {
      if (!toastShown) {
        const errorMsg =
          message && message.trim() !== ""
            ? message
            : "Login failed. Invalid or expired token."
        setErrorMessage(errorMsg)
        setToastShown(true)

        setTimeout(() => setToastShown(false), TIMING.ERROR_RESET_DELAY)
        setTimeout(() => {
          window.location.href = "/login"
        }, TIMING.REDIRECT_DELAY)
      }
    },
    [toastShown]
  )

  const processTokenLogin = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/${subDomain}/auth/login-as-token?token=${isToken}`
      )

      if (response.data.status) {
        handleTokenSuccess(response.data.data, response.data.message)
      } else {
        handleTokenError(response.data.message)
      }
    } catch (err) {
      handleTokenError("Network error or server unavailable. Please try again.")
    }
  }, [isToken, subDomain, handleTokenSuccess, handleTokenError])

  // Reusable Close All Button component
  const CloseAllButton = useCallback(
    () => (
      <button
        onClick={closeAllToasts}
        style={{
          position: "fixed",
          top: "10px",
          right: "10px",
          zIndex: 9999,
          backgroundColor: "#dc3545",
          color: "white",
          border: "none",
          borderRadius: "4px",
          padding: "8px 12px",
          fontSize: "12px",
          cursor: "pointer",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
        onMouseOver={e => (e.target.style.backgroundColor = "#c82333")}
        onMouseOut={e => (e.target.style.backgroundColor = "#dc3545")}
      >
        Close All
      </button>
    ),
    [closeAllToasts]
  )

  const ErrorMessage = useCallback(
    () => (
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#f8d7da",
          color: "#721c24",
          border: "1px solid #f5c6cb",
          borderRadius: "4px",
          padding: "15px 20px",
          fontSize: "14px",
          zIndex: 10000,
          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          maxWidth: "400px",
          textAlign: "center",
        }}
      >
        {errorMessage}
      </div>
    ),
    [errorMessage]
  )

  // Check if user is ready and trigger socket initialization
  useEffect(() => {
    const checkUserReady = () => {
      const currentUser = getCurrentUser()
      const company = localStorage.getItem("company_name")
      if (currentUser?.id && company) {
        setUserReady(true)
      }
    }

    // Check immediately
    checkUserReady()

    // Also check periodically for 5 seconds after mount (in case of login)
    const interval = setInterval(checkUserReady, 500)
    setTimeout(() => clearInterval(interval), 5000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (!userReady || socketInitialized) {
      return
    }

    const currentUser = getCurrentUser()
    const company = localStorage.getItem("company_name")

    if (currentUser?.id && company) {
      const socket = io(SOCKET_URL)
      const eventName = `${company}-${currentUser.id}`

      socket.on("connect", () => {
        setSocketInitialized(true)
      })

      const handleSocketMessage = (type, data) => {
        switch (type) {
          case "new_message":
            const messageText =
              data.body ||
              (data.image
                ? "sent you an image"
                : data.video
                ? "sent you a video"
                : "")
            showNotificationToast(`${data.message_user.name}: ${messageText}`)
            break

          case "notification":
            showNotificationToast(data)
            break

          case "lead_matured":
            try {
              const notificationData =
                typeof data === "string" ? JSON.parse(data) : data
              // Dispatch a custom event to open EditLeadModal
              const event = new CustomEvent("openEditLeadModal", {
                detail: { leadId: notificationData.lead_id },
              })
              window.dispatchEvent(event)
            } catch (e) {
              console.error("Error processing lead_matured event:", e)
            }
            break

          case "refresh":
            window.location.reload()
            break

          case "logout":
            setCurrentUser("")
            localStorage.setItem("DashboardData", "")
            localStorage.setItem("LastUpdated", "")
            localStorage.setItem("NavCounters", "")
            setSocketInitialized(false)
            setUserReady(false)
            history.push("/login")
            break

          default:
            break
        }
      }

      socket.on(eventName, handleSocketMessage)

      return () => {
        socket.off(eventName, handleSocketMessage)
        socket.disconnect()
      }
    }
  }, [userReady, showNotificationToast, history])

  // Handle token login only once when component mounts
  useEffect(() => {
    if (isToken !== undefined) {
      localStorage.setItem("company_name", subDomain)
      setCurrentUser("")
      processTokenLogin()
    }
  }, [isToken, subDomain, processTokenLogin])

  if (isToken !== undefined) {
    return (
      <>
        <ToastContainer />
        {errorMessage && <ErrorMessage />}
        {showCloseAllButton && <CloseAllButton />}
        <Loader />
      </>
    )
  } else {
    if (
      getCurrentUser() === null &&
      window.location.pathname !== "/login" &&
      window.location.pathname !== "/register" &&
      window.location.pathname !== "/forgot-password" &&
      window.location.pathname !== "/reset-password"
    ) {
      // history.push('/login')
      window.location.href = "/login"
    } else {
      return (
        <React.Fragment>
          <ToastContainer />
          {showCloseAllButton && <CloseAllButton />}
          {getCurrentUser() && <IdleTimer />}
          {getCurrentUser() && <GlobalLeadModal />}
          <Router>
            <CustomSwitch>
              {authRoutes.map((route, idx) => (
                <Authmiddleware
                  path={route.path}
                  layout={NonAuthLayout}
                  component={route.component}
                  key={idx}
                  isAuthProtected={false}
                />
              ))}
              {userRoutes.map((route, idx) => (
                <Authmiddleware
                  path={route.path}
                  layout={Layout}
                  component={route.component}
                  key={idx}
                  isAuthProtected={true}
                  exact
                />
              ))}
              <Redirect to="/404" />
              <Redirect to="/500" />
            </CustomSwitch>
          </Router>
        </React.Fragment>
      )
    }
  }
}

App.propTypes = {
  layout: PropTypes.any,
}

const mapStateToProps = state => {
  return {
    layout: state.Layout,
  }
}

export default connect(mapStateToProps, null)(App)
